import { useCallback, useEffect, useMemo, useState } from "react";

interface UseStickyFilteredItemsProps<TItem, TPayload = unknown> {
    items: TItem[];
    showUncompleted: boolean;
    getItemId: (item: TItem) => string;
    payloadsMap: Record<string, TPayload | undefined>;
    additionalFilter?: (item: TItem) => boolean;

    isIncomplete?: (payload: TPayload | undefined) => boolean;
}

export const useStickyFilteredItems = <TItem, TPayload>({
    items,
    showUncompleted,
    getItemId,
    payloadsMap,
    additionalFilter,
    isIncomplete,
}: UseStickyFilteredItemsProps<TItem, TPayload>) => {
    const [stickyIds, setStickyIds] = useState<Set<string>>(new Set());

    const checkIncomplete = useCallback(
        (item: TItem) => {
            const id = getItemId(item);
            const payload = payloadsMap[id];
            return isIncomplete ? isIncomplete(payload) : payload === undefined;
        },
        [getItemId, payloadsMap, isIncomplete],
    );

    useEffect(() => {
        if (showUncompleted) {
            setStickyIds((prev) => {
                const next = new Set(prev);

                items.forEach((item) => {
                    if (checkIncomplete(item)) {
                        next.add(getItemId(item));
                    }
                });
                return next;
            });
        } else {
            setStickyIds(new Set());
        }
    }, [showUncompleted, items]);

    const filteredItems = useMemo(() => {
        let list = items;

        if (additionalFilter) {
            list = list.filter(additionalFilter);
        }

        if (showUncompleted) {
            list = list.filter((item) => {
                const id = getItemId(item);
                return checkIncomplete(item) || stickyIds.has(id);
            });
        }

        return list;
    }, [
        items,
        additionalFilter,
        showUncompleted,
        getItemId,
        checkIncomplete,
        stickyIds,
    ]);

    return { filteredItems, stickyIds };
};
